import client from './client';
import { PhotosPage, UploadResult } from '../types';
import { reportPerformance } from '../lib/performanceMetrics';

const countRefreshAt = new Map<string, number>();

export const photosApi = {
  list: async (eventId: string, cursor?: string) => {
    const started = performance.now();
    const response = await client.get<PhotosPage>(`/events/${eventId}/photos`, {
      params: { ...(cursor ? { cursor } : {}), view: 'grid' },
    });
    if (!cursor) reportPerformance('gallery_response_ms', performance.now() - started);
    return response.data;
  },

  poll: async (eventId: string, since: string, refreshIds: string[] = []): Promise<PhotosPage> => {
    const includeCounts = Date.now() - (countRefreshAt.get(eventId) ?? 0) >= 30_000;
    const result: PhotosPage = { photos: [], nextCursor: null, hasMore: false };
    let cursor: string | null = null;
    do {
      const response = await client.get<PhotosPage>(`/events/${eventId}/photos`, {
        params: { since, ...(cursor ? { cursor } : {}), ...(refreshIds.length ? { refreshIds: refreshIds.slice(0, 100).join(',') } : {}), view: 'grid', includeCounts: includeCounts && !cursor },
      });
      const data: PhotosPage = response.data;
      if (!cursor) {
        Object.assign(result, data);
        if (includeCounts) {
          // Bound retained event metadata for long-lived sessions.
          if (countRefreshAt.size >= 128) countRefreshAt.delete(countRefreshAt.keys().next().value!);
          countRefreshAt.set(eventId, Date.now());
        }
      } else result.photos.push(...data.photos);
      cursor = data.hasMore ? data.nextCursor : null;
    } while (cursor);
    result.nextCursor = null;
    result.hasMore = false;
    return result;
  },

  /** All displayable photo ids for the event — used by "Select all". */
  listIds: (eventId: string) =>
    client
      .get<{ ids: string[] }>(`/events/${eventId}/photos/ids`)
      .then((r) => r.data.ids),

  delete: (eventId: string, photoId: string) =>
    client.delete(`/events/${eventId}/photos/${photoId}`).then((r) => r.data),

  batchDelete: (eventId: string, ids: string[]) =>
    client.delete(`/events/${eventId}/photos`, { data: { ids } }).then((r) => r.data),

  toggleHighlight: (eventId: string, photoId: string) =>
    client
      .patch<{ isHighlight: boolean }>(`/events/${eventId}/photos/${photoId}/highlight`)
      .then((r) => r.data),

  batchSetHighlight: (eventId: string, ids: string[], isHighlight: boolean) =>
    client
      .patch<{ updated: number; isHighlight: boolean }>(`/events/${eventId}/photos/highlight`, { ids, isHighlight })
      .then((r) => r.data),

  upload: (eventId: string, files: File[], onProgress?: (pct: number) => void) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('photos', f));
    return client
      .post<{ results: UploadResult[] }>(`/events/${eventId}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data.results);
  },

  uploadSingle: (eventId: string, file: File, onProgress?: (pct: number) => void, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append('photos', file);
    return client
      .post<{ results: UploadResult[] }>(`/events/${eventId}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data.results[0]);
  },

  thumbnailUrl: (photoId: string) => `/api/v1/photos/${photoId}/thumbnail`,
  originalUrl: (photoId: string) => `/api/v1/photos/${photoId}/original`,
};
